import { bulkManualSchedulerUtils } from '@/pages/KInterviewScheduler/BulkManualScheduler/bulkManualScheduler.utils';
import { KText } from '@/ui-library/components';
import { Flex } from '@chakra-ui/react';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { SchedulerDetailsConstants } from '../../SchedulerDetails.constants';
import { CalEventContent } from './CalEventContent';

export const ManualBookedContent = ({
  eventInfo,
  registerForUpdates,
  interviewDataRef,
}) => {
  const { event } = eventInfo || {};

  // these are for bulk manual
  // NOTE:
  // In bulk manual mode, properties like isActive/currentStepIndex and the
  // navigation callback are placed on the event root (not inside extendedProps).
  // For regular booked slots, these live under extendedProps. Read from both to
  // support both cases.
  const ext = event?.extendedProps ?? {};
  // Prefer extendedProps first (FullCalendar can strip unknown root keys during rendering)
  const isActive = ext.isActive ?? event?.isActive ?? true;
  const currentStepIndex = ext.currentStepIndex ?? event?.currentStepIndex;
  const interviewerStatus = ext.interviewerStatus ?? event?.interviewerStatus;
  const isOriginalSlotModified =
    ext.isOriginalSlotModified ?? event?.isOriginalSlotModified;
  const updateInterviewDataCallback =
    event?.updateInterviewDataCallback ?? ext.updateInterviewDataCallback;

  const {
    datesSet,
    interviewName,
    bulkBookedEvents = [],
    interviewers = [],
  } = interviewDataRef?.current ?? {};

  const [, forceUpdate] = useState({});

  const showErrorCondition =
    isActive &&
    Array.isArray(interviewers) &&
    interviewerStatus &&
    !isOriginalSlotModified;

  // this is only for bulk manual
  let showError = showErrorCondition
    ? interviewers.some((interviewer) => {
        const { has_exceeded_max_workload = false, is_unavailable = false } =
          interviewerStatus?.[interviewer] ?? {};
        return has_exceeded_max_workload || is_unavailable;
      })
    : false;

  useEffect(() => {
    const unregisterBookedSlot = registerForUpdates('interviewName', () =>
      forceUpdate({})
    );
    const unregisterDatesSet = registerForUpdates('datesSet', () =>
      forceUpdate({})
    );
    return () => {
      unregisterBookedSlot();
      unregisterDatesSet();
    };
  }, [registerForUpdates]);

  const { title, startStr, endStr } = event ?? {};
  const start = startStr;
  const end = endStr;

  // Calculate the duration in minutes
  const timezone = datesSet?.timeZone;
  const startMoment = moment.tz(start, timezone);
  const endMoment = moment.tz(end, timezone);
  const durationInMinutes = Math.round(
    (endMoment.valueOf() - startMoment.valueOf()) / (1000 * 60)
  );

  const hour = SchedulerDetailsConstants.MAX_HOURS;

  // Determine if the duration exceeds 1440 minutes (24 hours)
  const isDurationExceeded = durationInMinutes > hour * 60;

  // Apply styles based on whether the duration is exceeded
  const backgroundColor = isDurationExceeded ? 'error-200' : 'primary-500';

  const displayTime = `${startMoment.format('h:mm A')} - ${endMoment.format(
    'h:mm A'
  )}`;

  const { isConflictingWithBulkBookedEvents } = bulkManualSchedulerUtils;

  const isConflicting = isConflictingWithBulkBookedEvents(bulkBookedEvents, {
    start,
    end,
    timezone,
  });

  let bgColor = undefined;

  if ((isConflicting && isActive) || showError) {
    bgColor = 'error-200';
  } else if (isActive) {
    bgColor = backgroundColor;
  }

  return (
    <Flex
      w="100%"
      h="100%"
      bg={bgColor}
      overflow="hidden"
      cursor="pointer"
      flexDir={isDurationExceeded ? 'column' : 'row'}
    >
      <CalEventContent
        title={isActive ? interviewName : title}
        time={displayTime}
        colors={{ bg: backgroundColor, color: 'base-white' }}
        // this is for non active bulk manual items
        onClick={() => {
          const activeEvent = bulkBookedEvents.find((ev) => ev.isActive);
          const currentInterviewIndex =
            typeof activeEvent?.currentStepIndex === 'number'
              ? activeEvent.currentStepIndex
              : bulkBookedEvents.findIndex((ev) => ev.isActive);

          if (isActive === false) {
            updateInterviewDataCallback({
              data: {
                ...interviewDataRef.current,
              },
              currentIndex: currentInterviewIndex,
              newIndex: currentStepIndex,
            });
          }
        }}
      />
      {isDurationExceeded && (
        <KText bg="error-100" color="base-white" textAlign="center">
          Max {hour} hour
        </KText>
      )}
    </Flex>
  );
};
